import { useEffect, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { AnimateNumber } from "motion-plus/react";
import { Info } from "@/components/ui/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";
import {
  getAccountObjectives,
  type AccountObjectives,
} from "@/lib/api/accounts";

/**
 * Withdrawal Roadmap — actionable per-day projection panel.
 *
 * Turns the firm's static rules (consistency %, payout threshold,
 * minimum profitable days) into "what should I do today?" numbers:
 *
 *   • Profit To Withdrawal — $remaining of payout floor
 *   • Safe Cap Today      — biggest single-day P&L allowed today
 *                           without breaching the consistency ratio
 *   • Daily Target        — per-day pace to clear the payout floor
 *                           inside the required-days window
 *
 * Followed by a forward headroom table that maps future total-banked
 * milestones to the biggest single safe day at each milestone — so
 * the operator can see "once I'm at $2,000 banked, I can safely book
 * a $353 day."
 *
 * Math source: packages/shared/src/risk/instant-funding.ts:145-156.
 * Same `threshold·T/(1−threshold)` algebra; computed in the worker
 * and returned over `/api/accounts/:id/objectives`.
 */
export function WithdrawalRoadmap({
  accountId,
  liveEquity: _liveEquity,
  className,
}: {
  accountId: string;
  liveEquity?: number | null;
  className?: string;
}): ReactNode {
  const [data, setData] = useState<AccountObjectives | null>(null);
  const [loading, setLoading] = useState(true);

  // Same 30 s cadence as TradingObjectives — payout/consistency numbers
  // only move on close, so a tight tick-driven refetch would just burn
  // requests. TanStack Query would dedupe the two consumers; the
  // current dashboard uses raw fetches so we accept the dual hit.
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const next = await getAccountObjectives(accountId);
        if (!cancelled) setData(next);
      } catch {
        // Endpoint is null-safe; failure leaves stale data on screen.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchOnce();
    const iv = setInterval(fetchOnce, 30_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [accountId]);

  if (loading && !data) {
    return (
      <div
        className={cn(
          "min-w-0 overflow-hidden rounded-xl border border-border/40 bg-card/40 p-3 sm:p-4",
          className,
        )}
      >
        <div className="mb-4 h-5 w-40 rounded bg-muted/40" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-28 rounded-lg border border-border/30 bg-muted/20"
            />
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const r = data.withdrawalRoadmap;
  const thresholdPct = data.consistency.thresholdPct;
  const existingBiggest = data.consistency.mostProfitableDayPnl;
  const ratio = data.consistency.totalProfits > 0 ? data.consistency.ratio : 0;

  // First milestone the operator hasn't crossed yet — used to
  // highlight the relevant row in the headroom table.
  const nextMilestoneIdx = r.headroom.findIndex(
    (h) => h.totalAtMilestoneUsd > r.realizedProfitUsd,
  );

  const hasPayout = r.payoutThresholdUsd != null;
  const hasMinDays = r.minProfitableDays != null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 80, damping: 18 }}
      className={cn(
        "min-w-0 overflow-hidden rounded-xl border border-border/40 bg-card/40 p-3 backdrop-blur-sm sm:p-4",
        className,
      )}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-sans text-sm font-medium text-foreground">
          <span className="text-accent">Withdrawal</span> Roadmap
        </h3>
        {hasMinDays ? (
          <DayBadge
            banked={r.profitableDaysBanked}
            required={r.minProfitableDays ?? 0}
          />
        ) : (
          <DayBadge banked={r.profitableDaysBanked} />
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <RoadmapCard
          title="Profit To Withdrawal"
          tooltip={
            hasPayout
              ? `Payout floor: ${fmtMoney(r.payoutThresholdUsd ?? 0)}. You've banked ${fmtMoney(r.realizedProfitUsd)} so far.`
              : "No payout threshold configured on this account — set payout_threshold_pct in account settings to enable the roadmap."
          }
          primary={
            hasPayout ? (
              <PrimaryUsd value={r.remainingToPayoutUsd ?? 0} />
            ) : (
              <span className="font-mono text-2xl font-semibold tabular-nums text-muted-foreground">
                —
              </span>
            )
          }
          subtitle={
            hasPayout
              ? `${fmtMoney(r.realizedProfitUsd)} of ${fmtMoney(r.payoutThresholdUsd ?? 0)} banked`
              : "Add a payout threshold to unlock"
          }
          progressPct={
            hasPayout && (r.payoutThresholdUsd ?? 0) > 0
              ? Math.min(
                  100,
                  (r.realizedProfitUsd / (r.payoutThresholdUsd ?? 1)) * 100,
                )
              : 0
          }
        />

        <RoadmapCard
          title="Safe Cap Today"
          tooltip={`Biggest single-day net P&L you can book today without the consistency ratio crossing ${thresholdPct.toFixed(0)}%. Formula: max(existing biggest day, threshold·T / (1 − threshold)).`}
          primary={
            r.safeTodayCapUsd != null ? (
              <PrimaryUsd value={r.safeTodayCapUsd} accent />
            ) : (
              <span className="font-mono text-2xl font-semibold tabular-nums text-muted-foreground">
                —
              </span>
            )
          }
          subtitle={
            existingBiggest > 0
              ? `Biggest Day So Far: ${fmtMoney(existingBiggest)}`
              : "No Closed Days Yet"
          }
          progressPct={
            r.safeTodayCapUsd != null
              ? Math.min(100, ratio * 100)
              : 0
          }
        />

        <RoadmapCard
          title="Daily Target"
          tooltip={
            hasMinDays
              ? `Per-day pace to clear ${fmtMoney(r.payoutThresholdUsd ?? 0)} across ${r.daysRemainingToPayoutRule ?? 0} remaining profitable days, clamped to today's safe cap so it never suggests a breach.`
              : `Profit still needed to reach the payout floor, clamped to today's safe cap.`
          }
          primary={
            r.dailyTargetUsd != null ? (
              <PrimaryUsd value={r.dailyTargetUsd} />
            ) : (
              <span className="font-mono text-2xl font-semibold tabular-nums text-muted-foreground">
                —
              </span>
            )
          }
          subtitle={
            hasMinDays
              ? `${r.daysRemainingToPayoutRule ?? 0} Profitable Days Remaining`
              : r.remainingToPayoutUsd != null && r.remainingToPayoutUsd > 0
                ? "Capped By Safe Cap Today"
                : "Payout Floor Cleared"
          }
          progressPct={
            r.dailyTargetUsd != null && r.safeTodayCapUsd != null && r.safeTodayCapUsd > 0
              ? Math.min(100, (r.dailyTargetUsd / r.safeTodayCapUsd) * 100)
              : 0
          }
        />
      </div>

      {r.headroom.length > 0 ? (
        <HeadroomTable
          rows={r.headroom}
          thresholdPct={thresholdPct}
          nextMilestoneIdx={nextMilestoneIdx}
        />
      ) : null}

      <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
        Biggest Single-Day P&amp;L Must Stay ≤ {thresholdPct.toFixed(0)}% Of Total
        Profit. As Your Total Grows, The Cap Grows With It.
      </p>
    </motion.div>
  );
}

/* ── Building blocks ─────────────────────────────────────────── */

function RoadmapCard({
  title,
  tooltip,
  primary,
  subtitle,
  progressPct,
}: {
  title: string;
  tooltip: string;
  primary: ReactNode;
  subtitle: string;
  progressPct: number;
}): ReactNode {
  return (
    <div className="rounded-lg border border-border/40 bg-muted/20 p-4">
      <div className="mb-3 flex items-center gap-1.5">
        <span className="font-sans text-sm font-medium text-foreground">
          {title}
        </span>
        <Tooltip>
          <TooltipTrigger
            type="button"
            aria-label={`About ${title}`}
            className="inline-flex cursor-help items-center text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
          >
            <Info className="h-3 w-3" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-pretty leading-snug">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="mb-3">{primary}</div>
      <ProgressBar pct={progressPct} />
      <div className="mt-2 text-[11px] text-muted-foreground">{subtitle}</div>
    </div>
  );
}

function PrimaryUsd({
  value,
  accent,
}: {
  value: number;
  accent?: boolean;
}): ReactNode {
  return (
    <span
      className={cn(
        "font-mono text-2xl font-semibold tabular-nums",
        accent ? "text-accent" : "text-foreground",
      )}
    >
      $
      <AnimateNumber format={{ maximumFractionDigits: 0 }}>
        {value}
      </AnimateNumber>
    </span>
  );
}

function ProgressBar({ pct }: { pct: number }): ReactNode {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted/60">
      <motion.div
        className="h-full rounded-full bg-gradient-to-r from-accent/70 via-accent to-accent"
        initial={false}
        animate={{ width: `${clamped}%` }}
        transition={{ type: "spring", stiffness: 80, damping: 18 }}
      />
    </div>
  );
}

function DayBadge({
  banked,
  required,
}: {
  banked: number;
  required?: number;
}): ReactNode {
  const label =
    required != null
      ? `${banked} / ${required} Profitable Days`
      : `${banked} Profitable Day${banked === 1 ? "" : "s"} Banked`;
  return (
    <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/30 px-2.5 py-1 text-[11px] text-muted-foreground">
      <span className="font-mono tabular-nums text-foreground">{label}</span>
    </div>
  );
}

function HeadroomTable({
  rows,
  thresholdPct,
  nextMilestoneIdx,
}: {
  rows: Array<{ totalAtMilestoneUsd: number; maxSafeDayUsd: number }>;
  thresholdPct: number;
  nextMilestoneIdx: number;
}): ReactNode {
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-border/40 bg-muted/10">
      <div className="border-b border-border/30 px-4 py-2">
        <span className="font-sans text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Forward Headroom
        </span>
        <span className="ml-2 text-[11px] text-muted-foreground">
          (At {thresholdPct.toFixed(0)}% Consistency Rule)
        </span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border/20 bg-muted/20">
            <th className="px-4 py-2 text-left font-sans text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Total Banked
            </th>
            <th className="px-4 py-2 text-right font-sans text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Biggest Safe Day
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isNext = i === nextMilestoneIdx;
            return (
              <tr
                key={row.totalAtMilestoneUsd}
                className={cn(
                  "border-b border-border/10 last:border-0 transition-colors",
                  isNext && "bg-accent/5",
                )}
              >
                <td
                  className={cn(
                    "px-4 py-2 font-mono text-sm tabular-nums",
                    isNext ? "text-accent" : "text-foreground",
                  )}
                >
                  {fmtMoney(row.totalAtMilestoneUsd)}
                </td>
                <td
                  className={cn(
                    "px-4 py-2 text-right font-mono text-sm font-semibold tabular-nums",
                    isNext ? "text-accent" : "text-foreground",
                  )}
                >
                  {fmtMoney(row.maxSafeDayUsd)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
